// xterm.js wired to process.stdin/stdout, so upstream's ProcessTerminal (and
// every other process.stdout writer) talks to the page like a real TTY.

import { ClipboardAddon } from "@xterm/addon-clipboard";
import { FitAddon } from "@xterm/addon-fit";
import { Unicode11Addon } from "@xterm/addon-unicode11";
import { WebLinksAddon } from "@xterm/addon-web-links";
import { Terminal } from "@xterm/xterm";
import { process } from "../node/process.ts";
import { type Appearance, currentAppearance, followReducedMotion, screenReaderMode, TERMINAL_THEMES } from "./appearance.ts";
import { installImagePaste, isBrowserPasteKey } from "./clipboard.ts";
import { filePathFromUri, requestOpenFile } from "./file-links.ts";
import { setTabTitle } from "../ui/page.ts";

export interface HostTerminal {
	term: Terminal;
	fit(): void;
	writeBanner(text: string): void;
	/** Pass typed text through `filter` before pi reads it (the touch keys' sticky Ctrl). */
	filterInput(filter: (data: string) => string): void;
}

/**
 * Keys a browser terminal reports ambiguously. pi-tui understands the
 * modifyOtherKeys / CSI-u forms (tui/src/keys.ts), which xterm.js 6.0 does not emit.
 */
function encodeSpecialKey(event: KeyboardEvent): string | undefined {
	if (event.type !== "keydown" || event.isComposing) return undefined;
	if (event.key === "Enter") {
		if (event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey) return "\x1b[13;2u";
		if (event.ctrlKey && !event.shiftKey && !event.altKey && !event.metaKey) return "\x1b[13;5u";
	}
	return undefined;
}

const BLOCK_ELEMENT = /[▀-▟]/, BLOCK_ELEMENTS = /([▀-▟])/;

/**
 * Block elements (U+2580–U+259F: pi's logo, bars, shades) fill their cell, as a native terminal draws
 * them. xterm's DOM renderer uses the font's glyphs, which stop short of the row's top and bottom and
 * split at the glyph's middle rather than the cell's, so stacked blocks had gaps and half blocks did
 * not meet. Each block element goes in a span of its own, which the renderer sizes to the cell, and
 * web/host-ui.css paints it in the text color in place of the glyph.
 */
function drawBlockElements(container: HTMLElement): void {
	const rows = container.querySelector(".xterm-rows");
	if (!rows) return;
	const draw = (row: Element) => {
		if (!BLOCK_ELEMENT.test(row.textContent ?? "")) return;
		for (const span of [...row.children]) {
			const text = span.textContent ?? "";
			if (!BLOCK_ELEMENT.test(text) || span.hasAttribute("data-pi-block")) continue;
			span.replaceWith(...text.split(BLOCK_ELEMENTS).filter(Boolean).map((part) => {
				const piece = span.cloneNode(false) as HTMLElement;
				piece.textContent = part;
				if (BLOCK_ELEMENT.test(part)) piece.dataset.piBlock = part.codePointAt(0)!.toString(16);
				return piece;
			}));
		}
	};
	// The renderer rebuilds a row's spans for output, and also directly on focus, blur, selection and
	// link hover (not all through onRender). Observer callbacks run before the next paint.
	new MutationObserver((records) => {
		for (const row of new Set(records.map((record) => record.target))) if (row.parentNode === rows) draw(row as Element);
	}).observe(rows, { childList: true, subtree: true });
}

export function createHostTerminal(container: HTMLElement): HostTerminal {
	const term = new Terminal({
		allowProposedApi: true,
		// An accessible copy of the screen, and new output announced (/accessibility).
		screenReaderMode: screenReaderMode(),
		fontFamily: '"JetBrains Mono", "SF Mono", Menlo, Consolas, "DejaVu Sans Mono", monospace',
		fontSize: 14,
		lineHeight: 1.1,
		macOptionIsMeta: true,
		// WCAG AA for every color pi or a program writes (pi's light "dim" is 3:1 on its own), as in
		// VS Code's terminal. Colors that already reach 4.5:1 are drawn as written.
		minimumContrastRatio: 4.5,
		scrollback: 10000,
		theme: TERMINAL_THEMES[currentAppearance()],
		windowOptions: { getCellSizePixels: true, getWinSizeChars: true },
		// OSC 8 hyperlinks (pi's terminal.hyperlinks setting). Without a handler xterm
		// asks for confirmation on every click. pi links tool paths as file:// URLs;
		// those open in the files panel. Every other protocol is ignored.
		linkHandler: {
			allowNonHttpProtocols: true,
			activate: (_event, uri) => {
				// While pi tracks the mouse (its fullscreen mode) it receives the same click and
				// opens the link itself through `open` (host/bin/index.ts), like in a real terminal.
				if (term.modes.mouseTrackingMode !== "none") return;
				if (/^https?:\/\//i.test(uri)) { window.open(uri, "_blank", "noopener"); return; }
				const path = filePathFromUri(uri);
				if (path !== undefined) requestOpenFile(path);
			},
		},
	});
	const fitAddon = new FitAddon();
	term.loadAddon(fitAddon);
	term.loadAddon(new Unicode11Addon());
	term.unicode.activeVersion = "11";
	term.loadAddon(new WebLinksAddon((_event, uri) => window.open(uri, "_blank", "noopener")));
	term.loadAddon(new ClipboardAddon());
	term.open(container);
	drawBlockElements(container);
	if (term.textarea) {
		installImagePaste(term.textarea, {
			input: (sequence) => process.stdin.pushInput(sequence),
			error: (message) => term.write(`\r\n\x1b[31mClipboard: ${message.replace(/[\x00-\x1f\x7f]/g, " ")}\x1b[0m\r\n`),
		});
		// xterm takes text that arrives without key presses (dictation, the emoji picker) only once the
		// last key pressed in the terminal is released there. A key that moves focus away, like the
		// Enter that opens a host dialog, is released elsewhere, so release it when focus comes back.
		const textarea = term.textarea;
		let keyHeld = false;
		// Capture, like xterm's own listeners: it stops the keys it handles from propagating.
		textarea.addEventListener("keydown", () => { keyHeld = true; }, true);
		textarea.addEventListener("keyup", () => { keyHeld = false; }, true);
		textarea.addEventListener("focus", () => {
			if (!keyHeld) return;
			keyHeld = false;
			textarea.dispatchEvent(new KeyboardEvent("keyup", { key: "Shift", code: "ShiftLeft" }));
		});
	}

	term.attachCustomKeyEventHandler((event) => {
		// Let the browser emit paste (including image files), instead of xterm
		// consuming Ctrl+V as a terminal control byte. Custom pi bindings still work.
		if (isBrowserPasteKey(event, navigator.platform)) return false;
		const sequence = encodeSpecialKey(event);
		if (sequence === undefined) return true;
		event.preventDefault();
		process.stdin.pushInput(sequence);
		return false;
	});

	// While pi's theme follows the terminal it enables light/dark reports (DECSET 2031). On a report it
	// queries the colors again (OSC 10/11/4), which xterm answers from the theme set here. The handlers
	// only observe: xterm still sees the sequences.
	let colorSchemeReports = false;
	const observeReports = (enabled: boolean) => (params: (number | number[])[]) => {
		if (params.includes(2031)) colorSchemeReports = enabled;
		return false;
	};
	term.parser.registerCsiHandler({ prefix: "?", final: "h" }, observeReports(true));
	term.parser.registerCsiHandler({ prefix: "?", final: "l" }, observeReports(false));
	document.addEventListener("pi-appearance", (event) => {
		const appearance = (event as CustomEvent<Appearance>).detail;
		term.options.theme = TERMINAL_THEMES[appearance];
		if (colorSchemeReports) process.stdin.pushInput(appearance === "light" ? "\x1b[?997;2n" : "\x1b[?997;1n");
	});

	let filter = (data: string) => data;
	// The cursor blinks unless the system asks for reduced motion, now and whenever that changes.
	followReducedMotion((reduce) => { term.options.cursorBlink = !reduce; });
	document.addEventListener("pi-screen-reader", (event) => { term.options.screenReaderMode = (event as CustomEvent<boolean>).detail; });

	term.onData((data) => process.stdin.pushInput(filter(data)));
	term.onBinary((data) => process.stdin.pushInput(data));
	term.onResize(({ cols, rows }) => {
		process.stdout.resize(cols, rows);
		process.stderr.resize(cols, rows);
		process.emit("SIGWINCH", "SIGWINCH");
	});
	term.onTitleChange((title) => {
		setTabTitle(title || "π");
		document.dispatchEvent(new CustomEvent("pi-terminal-title", { detail: title || "π" }));
	});

	// A real TTY translates "\n" to "\r\n" (ONLCR) unless the program switched
	// the terminal to raw mode, which pi-tui does while it is running.
	const sink = (data: string) => term.write(process.stdin.isRaw ? data : data.replace(/(?<!\r)\n/g, "\r\n"));
	process.stdout.setSink(sink);
	process.stderr.setSink(sink);

	const fit = () => {
		try {
			fitAddon.fit();
		} catch {
			// Container not laid out yet.
		}
	};
	new ResizeObserver(() => fit()).observe(container);
	fit();
	process.stdout.resize(term.cols, term.rows);
	process.stderr.resize(term.cols, term.rows);
	term.focus();

	return {
		term,
		fit,
		writeBanner(text: string) {
			term.write(`\r\n\x1b[2m${text.replace(/\n/g, "\r\n")}\x1b[0m\r\n`);
		},
		filterInput(next) { filter = next; },
	};
}
